import React, {useEffect, useState} from "react";
import ClinicOverviewForm from "./ClinicOverviewForm";
import clinicService from "../../../../../services/clinic.service";
import {useParams} from "react-router-dom";
import Loading from "../../../../common/Loading";
import Swal from 'sweetalert2';

const ClinicOverview = ({updateLogo}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [clinic, setClinic] = useState({});
    const {clinicId} = useParams();
    const [isStatusLoading, setIsStatusLoading] = useState(false);

    useEffect(() => {
        getClinicData();
    }, [clinicId, updateLogo]);

    const getClinicData = () => {
        setIsLoading(true);
        clinicService
            .getClinicById(clinicId)
            .then((clinic) => {
                setClinic(clinic.data);
                updateLogo(clinic.data.logo);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
            });
    };

    const handleToggleStatus = () => {
        const action = clinic.status === 'ON' ? 'OFF' : 'ON';
        const iconClass = clinic.status === 'ON' ? 'bi-power-off' : 'bi-power';
        Swal.fire({
            title: 'Are you sure?',
            html: `You're about to turn <strong>${action}</strong> the ${clinic.name}`,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: `
                <i class="bi bi-x"></i>
                Close
            `,
            confirmButtonText: `
            <i class="bi ${iconClass} me-1"></i>
            Yes, turn ${clinic.status === 'ON' ? 'OFF' : 'ON'}
            `,
            buttonsStyling: false,
            customClass: {
                confirmButton: `btn ${clinic.status === 'ON' ? 'btn-danger' : 'btn-success'} me-2 btn-sm`,
                cancelButton: 'btn btn-secondary btn-sm',
                title: `card-title`,
                html: `small`
            }
        }).then((result) => {
            try {
                if (result.isConfirmed) {
                    setIsStatusLoading(true);
                    const newStatus = clinic.status === 'ON' ? 'OFF' : 'ON';

                    // Call service with proper parameters
                    clinicService.patchClinic(clinic.id, newStatus)
                        .then((clinic) => {
                            setClinic(prev => ({...prev, status: newStatus}));
                            setIsLoading(false);
                            getClinicData();
                        })
                        .catch((err) => {
                            console.log(err);
                            setIsLoading(false);
                        });
                }
            } catch (error) {
            } finally {
                setIsStatusLoading(false);
            }
        });
    };

    return (
        <div className="card card-border shadow-none">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="card-title">
                        <i className="bi bi-hospital me-2"></i>
                        Clinic Overview
                    </div>
                    <div>
                        {isEditing ? (
                            <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => setIsEditing(false)}
                                title="Edit Clinic information"
                            >
                                <i className="bi bi-x me-1"></i> Cancel
                            </button>
                        ) : (
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setIsEditing(true)}
                            >
                                <i className="bi bi-pencil-square me-1"></i> Edit
                            </button>
                        )}
                        &nbsp;
                        <button
                            className={`btn btn-sm ${clinic.status === 'ON' ? 'btn-danger' : 'btn-success'}`}
                            // onClick={handleToggleClick}
                            onClick={handleToggleStatus}
                            disabled={isStatusLoading}
                            title="Turn ON / OFF clinic"
                        >
                            {isStatusLoading ? (
                                <span className="spinner-border spinner-border-sm" role="status"
                                      aria-hidden="true"> </span>
                            ) : (
                                <i className="bi bi-power me-1"></i>
                            )}
                            {clinic.status === 'ON' ? 'TURN OFF' : 'TURN ON'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body">
                {isLoading ? (
                    <Loading/>
                ) : isEditing ? (
                    <ClinicOverviewForm clinicinfo={clinic} getClinicData={getClinicData}/>
                ) : (
                    <>
                        <div className="row">
                            <div className="col-md-12 col-lg-12 col-xl-12">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="card-title fw-semibold">Basic Information</div>
                                    <div className="badge service-status status-completed text-success fw-bold small"># {clinic.number}</div>
                                </div>

                                <dl className="row mb-0">
                                    <dt className="col-sm-2 fw-normal text-muted">Status</dt>
                                    <dd className="col-sm-10">
                                        <span className={`badge bg-${clinic.status === "ON" ? "success" : "danger"}`}>
                                            {clinic.status || '-'}
                                        </span>
                                    </dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Type</dt>
                                    <dd className="col-sm-10">{clinic.clinicType || '-'}</dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Name</dt>
                                    <dd className="col-sm-10">{clinic.name || '-'}</dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Start Date</dt>
                                    <dd className="col-sm-10">{clinic.dateStarted}</dd>
                                </dl>
                            </div>
                            <hr/>
                            <div className="col-md-12 col-lg-12 col-xl-12">
                                <div className="card-title">
                                    <div className="fw-semibold">Contact Information</div>
                                </div>
                                <dl className="row mb-0">
                                    <dt className="col-sm-2 fw-normal text-muted">Email</dt>
                                    <dd className="col-sm-10">{clinic.email || '-'}</dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Phone</dt>
                                    <dd className="col-sm-10">{clinic.phone || '-'}</dd>

                                    <dt className="col-sm-2 fw-normal text-muted">SMS</dt>
                                    <dd className="col-sm-10">{clinic.sms || '-'}</dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Website</dt>
                                    <dd className="col-sm-10">
                                        {clinic.website ? (
                                            <a href={clinic.website} target="_blank" rel="noopener noreferrer">
                                                {clinic.website} <i className="bi bi-window"></i>
                                            </a>
                                        ) : '-'}
                                    </dd>
                                </dl>
                            </div>
                            <hr/>
                            <div className="col-12">
                                <div className="card-title">
                                    <div className="fw-semibold">Additional Information</div>
                                </div>
                                <dl className="row mb-0">
                                    <dt className="col-sm-2 fw-normal text-muted">Signature</dt>
                                    <dd className="col-sm-10">{clinic.signature || '-'}</dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Logo</dt>
                                    <dd className="col-sm-10">
                                        {clinic.logo ? (
                                            <span className="text-break">
                                            <a href={clinic.logo} target="_blank" rel="noopener noreferrer">
                                            {clinic.logo} <i className="bi bi-window"></i>
                                            </a>
                                        </span>
                                        ) : '-'}
                                    </dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Created</dt>
                                    <dd className="col-sm-10">{clinic.createdAt}</dd>

                                    <dt className="col-sm-2 fw-normal text-muted">Modified</dt>
                                    <dd className="col-sm-10">
                                        <span className="d-flex align-items-center">
                                            <i className="bi bi-clock-history me-2 text-muted"></i>
                                            {clinic.modifiedAt}
                                        </span>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ClinicOverview;
